import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import { parseCalculatorMapContextProjectionV1 } from "./map-calculator-context.js";
import {
  dispatchCharacterActivityTargetChange,
  dispatchFocusWorldPointRequest,
  dispatchLootConditionOptionSelect,
  dispatchZoneInfoTabChange,
  dispatchZoneLootProfileChange,
} from "./map-panel-event-contract.js";
import { mapText, siteText } from "./map-i18n.js";
import { buildInfoViewModel } from "./map-info-state.js";
import {
  buildLocationBookmarkAction,
  copyLocationBookmark,
  createLocationBookmark,
  locationBookmarkSectionMarkup,
} from "./map-location-bookmark.js";
import { preferredPointLabelForLayerSamples } from "./map-overview-facts.js";
import { FISHYMAP_ZONE_CATALOG_READY_EVENT } from "./map-zone-catalog-live.js";
import { loadZoneLootSummary, zoneRgbFromSelection } from "./map-zone-loot-summary.js";
import { FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT } from "./map-trade-npc-catalog-live.js";
import { tradeNpcPortraitMarkup } from "./map-trade-npc-presentation.js";
import {
  TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY,
  TRADE_NPC_CLASS_TRADER,
} from "./map-trade-npc-contract.js";
import { zoneMaskLineageSectionMarkup } from "./map-zone-mask-lineage.js";
import {
  provenanceClaimsText,
  provenanceRailMarkup,
} from "../js/components/provenance-indicator.js";
import { provenanceIndicatorColorForTone } from "../js/components/provenance-tone.js";
import {
  calculatorDataEnumLabel,
  calculatorDataLocale,
  calculatorDataText,
} from "../js/components/calculator-data-i18n.js";
import {
  calculatorLootProvenanceAuthoredValueText,
  calculatorLootProvenanceConditionSetText,
  calculatorLootProvenanceFactLabel,
} from "../js/components/calculator-loot-provenance-presentation.js";
import { presentCalculatorClaimDateV1 } from "../js/components/calculator-claim-date-presentation.js";
import {
  itemGradeTone,
  lootItemRowMarkup,
  renderLootMetricCell,
} from "../js/components/loot-item-row.js";
import {
  calculatorLootRateBasisPresentation,
  parseCalculatorLootRateDisplayMode,
} from "../js/components/calculator-loot-rate-presentation.js";
import {
  lootConditionSwitcherActionFromTrigger,
  lootConditionSwitcherMarkup,
} from "../js/components/loot-condition-switcher.js";
import "./hotspot-model-viewer-element.js";

const INFO_PANEL_TAG_NAME = "fishymap-info-panel";
const FISHYMAP_V2_EVENT = "fishymap:v2";
const HTMLElementBase = globalThis.HTMLElement ?? class {};
const POINT_SAMPLE_PAGE_SIZE = 50;
const SELECTION_HISTORY_LIMIT = 8;
const TRADE_MANAGER_FILTER_ALL = "all";
const TRADE_MANAGER_FILTER_IDS = new Set([
  TRADE_MANAGER_FILTER_ALL,
  TRADE_NPC_CLASS_TRADER,
  TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY,
]);

function upgradeProperty(element, property) {
  if (!Object.prototype.hasOwnProperty.call(element, property)) {
    return;
  }
  const value = element[property];
  delete element[property];
  element[property] = value;
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>\"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function jsonAttribute(value) {
  return escapeHtml(JSON.stringify(value));
}

function infoViewSignalsFromProjection(projection) {
  return {
    _map_ui: projection?.ui || {},
    _map_runtime: projection?.runtime || {},
  };
}

function setBooleanProperty(element, propertyName, value) {
  if (!element) {
    return;
  }
  element[propertyName] = Boolean(value);
}

function componentEventTarget(component) {
  if (typeof component?.dispatchEvent === "function") {
    return component;
  }
  return component?._shell || null;
}

function focusWorldPointRequestTarget(value, historyBehavior = "append") {
  const worldX = Number(value?.worldX);
  const worldZ = Number(value?.worldZ);
  if (!Number.isFinite(worldX) || !Number.isFinite(worldZ)) {
    return null;
  }
  return {
    elementKind: trimString(value?.elementKind),
    characterIdentityKey: trimString(value?.characterIdentityKey),
    worldX,
    worldZ,
    pointKind: trimString(value?.pointKind).toLowerCase(),
    pointLabel: trimString(value?.pointLabel),
    historyBehavior,
  };
}

function setTextContent(element, text) {
  if (!element) {
    return;
  }
  element.textContent = String(text ?? "");
}

function setMarkup(element, renderKey, markup) {
  if (!element) {
    return;
  }
  const normalizedKey = String(renderKey ?? "");
  if (element.dataset.renderKey === normalizedKey) {
    return;
  }
  element.dataset.renderKey = normalizedKey;
  element.innerHTML = String(markup ?? "");
}

function setOptionalAttribute(element, name, value) {
  if (!element) {
    return;
  }
  const normalized = trimString(value);
  if (!normalized) {
    element.removeAttribute?.(name);
    return;
  }
  element.setAttribute?.(name, normalized);
}

function locationBookmarkSectionFromViewModel(viewModel) {
  for (const pane of Array.isArray(viewModel?.panes) ? viewModel.panes : []) {
    const section = (Array.isArray(pane?.sections) ? pane.sections : [])
      .find((candidate) => trimString(candidate?.kind) === "location-bookmark");
    if (section) {
      return section;
    }
  }
  return null;
}

function spriteIcon(name, sizeClass = "size-5") {
  return `<svg class="fishy-icon ${sizeClass}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${name}"></use></svg>`;
}

function trimString(value) {
  const normalized = String(value ?? "").trim();
  return normalized || "";
}

export function visibleCharacterActivityIdentity(viewModel, projection) {
  if (trimString(viewModel?.activePaneId) !== "character") {
    return "";
  }
  const zoneInfo = projection?.ui?.windowUi?.zoneInfo;
  if (zoneInfo?.open !== true || zoneInfo?.collapsed === true) {
    return "";
  }
  const activity = (viewModel?.activePane?.sections || []).find(
    (section) => trimString(section?.kind) === "character-activity",
  );
  return trimString(activity?.identityKey);
}

function pointSampleZoneIndicatorMarkup(zone) {
  if (zone?.zoneKind === "partial") {
    const style = trimString(zone?.swatchRgb)
      ? ` style="--fishymap-layer-fact-rgb:${escapeHtml(zone.swatchRgb)};"`
      : "";
    return `<svg class="fishy-icon size-4 fishymap-point-sample-zone-icon" viewBox="0 0 24 24" aria-hidden="true"${style}><use width="100%" height="100%" href="#fishy-ring-partial"></use></svg>`;
  }
  return trimString(zone?.swatchRgb)
    ? `<span class="fishymap-layer-fact-swatch" style="--fishymap-layer-fact-rgb:${escapeHtml(zone.swatchRgb)};"></span>`
    : "";
}

function factIconMarkup(fact) {
  if (trimString(fact?.swatchRgb)) {
    return `<span class="fishymap-layer-fact-swatch" style="--fishymap-layer-fact-rgb:${escapeHtml(fact.swatchRgb)};" aria-hidden="true"></span>`;
  }
  return spriteIcon(fact?.icon || "information-circle", "size-4");
}

function factFocusAction(fact) {
  const action = fact?.action;
  if (action?.kind !== "focus-world-point") {
    return null;
  }
  const focusWorldPoint = action.focusWorldPoint;
  const worldX = Number(focusWorldPoint?.worldX);
  const worldZ = Number(focusWorldPoint?.worldZ);
  if (!Number.isFinite(worldX) || !Number.isFinite(worldZ)) {
    return null;
  }
  return {
    worldX,
    worldZ,
    elementKind:
      trimString(focusWorldPoint?.elementKind || fact?.elementKind || fact?.targetKind),
    pointKind: trimString(focusWorldPoint?.pointKind),
    pointLabel: trimString(focusWorldPoint?.pointLabel),
  };
}

function tabButtonMarkup(tab, activePaneId) {
  const isActive = tab.id === activePaneId;
  return `
    <button
      class="tab gap-2 ${isActive ? "tab-active" : ""}"
      type="button"
      role="tab"
      aria-selected="${isActive ? "true" : "false"}"
      data-zone-info-tab="${escapeHtml(tab.id)}"
      aria-label="${escapeHtml(tab.summary || tab.label)}"
      data-fishy-tooltip="${escapeHtml(tab.summary || tab.label)}"
    >
      ${spriteIcon(tab.icon || "information-circle", "size-4")}
      <span class="truncate">${escapeHtml(tab.label)}</span>
    </button>
  `;
}

function focusWorldPointButtonMarkup(focusAction, label) {
  if (!focusAction) {
    return "";
  }
  const normalizedLabel = trimString(label);
  const ariaLabel = normalizedLabel
    ? mapText("info.trade.focus_target", { name: normalizedLabel })
    : mapText("info.trade.focus_target_generic");
  return `<button
    type="button"
    class="btn btn-ghost btn-sm h-auto min-h-0 self-stretch rounded-none rounded-r-box border-0 px-2 text-base-content/55 hover:bg-base-200 hover:text-base-content"
    data-fishy-focus-world-point="true"
    data-focus-element-kind="${escapeHtml(focusAction.elementKind)}"
    data-focus-world-x="${escapeHtml(focusAction.worldX)}"
    data-focus-world-z="${escapeHtml(focusAction.worldZ)}"
    data-focus-point-kind="${escapeHtml(focusAction.pointKind)}"
    data-focus-point-label="${escapeHtml(focusAction.pointLabel)}"
    aria-label="${escapeHtml(ariaLabel)}"
    data-fishy-tooltip="${escapeHtml(ariaLabel)}"
  >${spriteIcon("right-fill", "size-4")}</button>`;
}

function normalizeTradeManagerFilter(value) {
  const normalized = trimString(value).toLowerCase();
  return TRADE_MANAGER_FILTER_IDS.has(normalized) ? normalized : TRADE_MANAGER_FILTER_ALL;
}

function tradeManagerFilterMarkup(activeFilter) {
  const selected = normalizeTradeManagerFilter(activeFilter);
  const options = [
    [TRADE_MANAGER_FILTER_ALL, mapText("info.trade.filter_all")],
    [TRADE_NPC_CLASS_TRADER, mapText("info.trade.filter_standard")],
    [
      TRADE_NPC_CLASS_IMPERIAL_FISHING_DELIVERY,
      mapText("info.trade.filter_imperial"),
    ],
  ];
  return `
    <div
      class="tabs tabs-box bg-base-200/80 p-1"
      role="tablist"
      aria-label="${escapeHtml(mapText("info.trade.filter_label"))}"
    >
      ${options
        .map(([value, label]) => `<button
          class="tab ${value === selected ? "tab-active" : ""}"
          type="button"
          role="tab"
          aria-selected="${value === selected ? "true" : "false"}"
          data-trade-manager-filter="${escapeHtml(value)}"
        >${escapeHtml(label)}</button>`)
        .join("")}
    </div>
  `;
}

function tradeManagerMultiplierMarkup(row) {
  const multiplierText = trimString(row?.saleMultiplierText);
  if (!multiplierText) {
    return "";
  }
  const fixed = row?.saleMultiplierKind === "fixed";
  const qualifier = mapText(
    fixed ? "info.trade.multiplier_fixed" : "info.trade.multiplier_final",
    { multiplier: multiplierText },
  );
  const explanation = mapText(
    fixed ? "info.trade.multiplier_fixed_help" : "info.trade.multiplier_final_help",
  );
  return `<span
    class="badge badge-sm whitespace-nowrap"
    tabindex="0"
    aria-label="${escapeHtml(explanation)}"
    data-fishy-tooltip="${escapeHtml(explanation)}"
  >${escapeHtml(qualifier)}</span>`;
}

function tradeManagerRowMarkup(row, groupId) {
  const regionLabel = trimString(row?.regionLabel);
  const distanceBonusText = trimString(row?.distanceBonusText);
  const npcName = trimString(row?.npcName) || "NPC";
  const focusAction = factFocusAction(row);
  const maximumBonus = groupId === "maximum-bonus";
  const distanceBonus = Number.isFinite(row?.distanceBonus) && row.distanceBonus >= 0
    ? row.distanceBonus
    : null;
  const distanceBadgeClass = maximumBonus
    ? "badge-success badge-soft"
    : distanceBonus == null
      ? "badge-neutral badge-outline"
      : "fishy-trade-distance-badge";
  const distanceBadgeStyle = !maximumBonus && distanceBonus != null
    ? ` style="--fishy-trade-distance-bonus:${escapeHtml(distanceBonus)}"`
    : "";
  return `
    <article class="fishymap-trade-manager-row" data-distance-group="${escapeHtml(groupId)}" data-trade-manager-class="${escapeHtml(row?.class)}">
      ${tradeNpcPortraitMarkup({ imageUrl: row?.imageUrl, badgeImageUrl: row?.badgeImageUrl, name: npcName, variant: "list" })}
      <div class="fishymap-trade-manager-identity">
        <span class="fishymap-trade-manager-name">${escapeHtml(npcName)}</span>
        ${
          regionLabel
            ? `<span class="fishymap-trade-manager-region">${escapeHtml(regionLabel)}</span>`
            : ""
        }
      </div>
      <div class="fishymap-trade-manager-values flex flex-col items-end gap-1 max-[420px]:col-start-2 max-[420px]:items-start">
        ${
          distanceBonusText
            ? `<span class="badge badge-sm whitespace-nowrap ${distanceBadgeClass}"${distanceBadgeStyle}>${escapeHtml(distanceBonusText)}</span>`
            : ""
        }
        ${tradeManagerMultiplierMarkup(row)}
      </div>
      ${focusWorldPointButtonMarkup(focusAction, npcName)}
    </article>
  `;
}

function tradeManagerSectionMarkup(section, { tradeManagerFilter = TRADE_MANAGER_FILTER_ALL } = {}) {
  const selectedFilter = normalizeTradeManagerFilter(tradeManagerFilter);
  const groups = (Array.isArray(section?.groups) ? section.groups : [])
    .map((group) => ({
      ...group,
      rows: (Array.isArray(group?.rows) ? group.rows : []).filter(
        (row) => selectedFilter === TRADE_MANAGER_FILTER_ALL || row?.class === selectedFilter,
      ),
    }))
    .filter((group) => group.rows.length > 0);
  return `
    <section class="fishymap-trade-manager-section space-y-3">
      ${
        trimString(section?.title)
          ? `<p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section.title)}</p>`
          : ""
      }
      ${tradeManagerFilterMarkup(selectedFilter)}
      ${
        groups.length
          ? `<div class="fishymap-trade-manager-groups">
              ${groups
                .map((group) => {
                  const groupId = trimString(group?.id);
                  const rows = group.rows;
                  return `
                    <section class="fishymap-trade-manager-group fishymap-trade-manager-group--${escapeHtml(groupId)}" data-distance-group="${escapeHtml(groupId)}">
                      <header class="fishymap-trade-manager-group-header">
                        <h3 class="fishymap-trade-manager-group-title">${escapeHtml(group?.title || "")}</h3>
                        <span class="badge badge-sm ${groupId === "maximum-bonus" ? "badge-success badge-soft" : "badge-neutral badge-outline"}">${escapeHtml(rows.length)}</span>
                      </header>
                      <div class="fishymap-trade-manager-list">${rows
                        .map((row) => tradeManagerRowMarkup(row, groupId))
                        .join("")}</div>
                    </section>
                  `;
                })
                .join("")}
            </div>`
          : `<p class="text-sm text-base-content/60">${escapeHtml(mapText("info.trade.filter_empty"))}</p>`
      }
    </section>
  `;
}

function factMarkup(fact) {
  const focusAction = factFocusAction(fact);
  const labelMarkup = focusAction
    ? `<button
        type="button"
        class="link link-primary text-left font-semibold"
        data-fishy-focus-world-point="true"
        data-focus-element-kind="${escapeHtml(focusAction.elementKind)}"
        data-focus-world-x="${escapeHtml(focusAction.worldX)}"
        data-focus-world-z="${escapeHtml(focusAction.worldZ)}"
        data-focus-point-kind="${escapeHtml(focusAction.pointKind)}"
        data-focus-point-label="${escapeHtml(focusAction.pointLabel)}"
      >${escapeHtml(fact.label)}</button>`
    : escapeHtml(fact.label);
  return `
    <div class="fishymap-overview-row">
      <span class="fishymap-overview-row-icon" aria-hidden="true">${factIconMarkup(fact)}</span>
      <span class="fishymap-overview-row-label">${labelMarkup}</span>
      <span class="fishymap-overview-row-value">${escapeHtml(fact.value)}</span>
    </div>
  `;
}

function focusWorldPointFromButton(button) {
  if (!button) {
    return null;
  }
  const worldX = Number(button.getAttribute("data-focus-world-x"));
  const worldZ = Number(button.getAttribute("data-focus-world-z"));
  if (!Number.isFinite(worldX) || !Number.isFinite(worldZ)) {
    return null;
  }
  return {
    worldX,
    worldZ,
    ...(trimString(button.getAttribute("data-focus-element-kind"))
      ? { elementKind: trimString(button.getAttribute("data-focus-element-kind")) }
      : {}),
    ...(trimString(button.getAttribute("data-focus-point-kind"))
      ? { pointKind: trimString(button.getAttribute("data-focus-point-kind")) }
      : {}),
    ...(trimString(button.getAttribute("data-focus-point-label"))
      ? { pointLabel: trimString(button.getAttribute("data-focus-point-label")) }
      : {}),
  };
}

function finiteCoordinate(value) {
  if (value == null) {
    return null;
  }
  if (typeof value === "string" && !value.trim()) {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizedSelectionPointKind(value) {
  const pointKind = trimString(value).toLowerCase();
  if (
    pointKind === "bookmark" ||
    pointKind === "waypoint" ||
    pointKind === "clicked" ||
    pointKind === "character"
  ) {
    return pointKind;
  }
  return "";
}

function selectionDetailsGeneration(value) {
  const generation = Number.parseInt(value, 10);
  return Number.isInteger(generation) && generation > 0 ? generation : 0;
}

function elementKindForSelectionTarget(target, fallbackPointKind = "") {
  const elementKind = trimString(target?.elementKind).toLowerCase();
  if (elementKind) {
    return elementKind;
  }
  switch (normalizedSelectionPointKind(fallbackPointKind || target?.pointKind)) {
    case "bookmark":
      return "bookmark";
    case "waypoint":
      return "waypoint";
    case "character":
      return "character";
    case "clicked":
      return "point";
    default:
      return "element";
  }
}

function detailsTargetFromSelection(selection) {
  const hasExplicitTarget = isPlainObject(selection?.detailsTarget);
  const source = hasExplicitTarget ? selection.detailsTarget : selection;
  const worldX = finiteCoordinate(source?.worldX);
  const worldZ = finiteCoordinate(source?.worldZ);
  if (worldX == null || worldZ == null) {
    return null;
  }
  const pointKind = normalizedSelectionPointKind(source?.pointKind || selection?.pointKind);
  const characterIdentityKey = trimString(source?.characterIdentityKey);
  const elementKind = elementKindForSelectionTarget(source, pointKind);
  return {
    elementKind,
    worldX,
    worldZ,
    pointKind,
    pointLabel: trimString(source?.pointLabel || (hasExplicitTarget ? "" : selection?.pointLabel)),
    historyBehavior: selectionHistoryBehavior(source?.historyBehavior),
    ...(elementKind === "character" && characterIdentityKey ? { characterIdentityKey } : {}),
  };
}

function selectionHistoryBehavior(value) {
  return trimString(value).toLowerCase() === "navigate" ? "navigate" : "append";
}

function coordinateSelectionKeyPart(value) {
  return Number(value).toFixed(3);
}

function selectionTargetKey(target, detailsGeneration = 0) {
  if (!target) {
    return "";
  }
  if (
    trimString(target.elementKind).toLowerCase() === "character" &&
    Number.isSafeInteger(detailsGeneration) &&
    detailsGeneration > 0
  ) {
    return `character-selection:${detailsGeneration}`;
  }
  return [
    target.elementKind || "element",
    coordinateSelectionKeyPart(target.worldX),
    coordinateSelectionKeyPart(target.worldZ),
  ].join(":");
}

function selectionTargetFallbackLabel(elementKind) {
  switch (trimString(elementKind).toLowerCase()) {
    case "bookmark":
      return mapText("info.status.bookmark");
    case "waypoint":
      return mapText("info.status.waypoint");
    case "character":
      return "Character";
    case "npc":
      return "NPC";
    case "hotspot":
      return "Hotspot";
    case "sample":
      return "Sample";
    default:
      return "";
  }
}

function selectionHistoryLabel(selection, { zoneCatalog = [], runtimeLayers = [] } = {}) {
  const target = detailsTargetFromSelection(selection);
  const targetPointLabel = trimString(target?.pointLabel);
  const pointLabel = trimString(targetPointLabel || selection?.pointLabel);
  const elementKind = trimString(target?.elementKind);
  const targetFallbackLabel = selectionTargetFallbackLabel(elementKind);
  if (targetFallbackLabel) {
    if (targetPointLabel) {
      return targetPointLabel;
    }
    return targetFallbackLabel;
  }
  const layerSamples = Array.isArray(selection?.layerSamples) ? selection.layerSamples : [];
  const layerLabel = preferredPointLabelForLayerSamples(layerSamples, {
    zoneCatalog,
    runtimeLayers,
  });
  if (layerLabel) {
    return layerLabel;
  }
  if (pointLabel) {
    return pointLabel;
  }
  const pointKind = normalizedSelectionPointKind(target?.pointKind || selection?.pointKind);
  switch (pointKind) {
    case "bookmark":
      return mapText("info.status.bookmark");
    case "waypoint":
      return mapText("info.status.waypoint");
    case "character":
      return "Character";
    case "clicked":
      return mapText("info.status.clicked");
    default:
      return mapText("info.status.no_selection");
  }
}

function selectionHistoryEntryFromSelection(
  selection,
  { zoneCatalog = [], runtimeLayers = [] } = {},
) {
  if (!selection || typeof selection !== "object") {
    return null;
  }
  const detailsGeneration = selectionDetailsGeneration(selection.detailsGeneration);
  const target = detailsTargetFromSelection(selection);
  const key = selectionTargetKey(target, detailsGeneration);
  if (!detailsGeneration || !key) {
    return null;
  }
  const label = selectionHistoryLabel(selection, { zoneCatalog, runtimeLayers });
  const pointKind = normalizedSelectionPointKind(selection.pointKind) || "clicked";
  return {
    key,
    detailsGeneration,
    historyBehavior: target.historyBehavior,
    label,
    target,
    worldX: target.worldX,
    worldZ: target.worldZ,
    pointKind: target.pointKind || pointKind,
    pointLabel: target.pointLabel || label,
  };
}

function runtimeLayersFromProjection(projection) {
  return Array.isArray(projection?.runtime?.catalog?.layers)
    ? projection.runtime.catalog.layers
    : [];
}

function runtimeLayersFromSelectionEventDetail(detail, projection) {
  return Array.isArray(detail?.state?.catalog?.layers)
    ? detail.state.catalog.layers
    : runtimeLayersFromProjection(projection);
}

function normalizeSelectionHistoryEntries(entries) {
  return (Array.isArray(entries) ? entries : []).filter((entry) => entry?.key);
}

function normalizeSelectionHistoryIndex(entries, index) {
  if (!entries.length) {
    return -1;
  }
  const normalized = Number.parseInt(index, 10);
  if (!Number.isInteger(normalized) || normalized < 0) {
    return entries.length - 1;
  }
  return Math.min(entries.length - 1, normalized);
}

function appendSelectionHistoryEntry(entries, index, entry) {
  const history = normalizeSelectionHistoryEntries(entries);
  if (!entry?.key) {
    return {
      entries: history.slice(-SELECTION_HISTORY_LIMIT),
      index: normalizeSelectionHistoryIndex(history, index),
    };
  }

  const currentIndex = normalizeSelectionHistoryIndex(history, index);
  if (currentIndex >= 0 && history[currentIndex]?.key === entry.key) {
    const updated = history.slice();
    updated[currentIndex] = entry;
    return { entries: updated, index: currentIndex };
  }

  const retained = currentIndex >= 0 ? history.slice(0, currentIndex + 1) : [];
  const next = [...retained, entry];
  const overflow = Math.max(0, next.length - SELECTION_HISTORY_LIMIT);
  const bounded = overflow ? next.slice(overflow) : next;
  return { entries: bounded, index: bounded.length - 1 };
}

function selectionHistoryTargetEntry(entries, index, direction) {
  const history = normalizeSelectionHistoryEntries(entries);
  const currentIndex = normalizeSelectionHistoryIndex(history, index);
  const targetIndex = currentIndex + (direction === "forward" ? 1 : -1);
  if (targetIndex < 0 || targetIndex >= history.length) {
    return null;
  }
  return history[targetIndex];
}

function selectionHistoryButtonLabel(entry) {
  return trimString(entry?.label) || mapText("info.history.unknown");
}

function historyTargetText(entry, messageKey) {
  const label = selectionHistoryButtonLabel(entry);
  return entry?.key ? mapText(messageKey, { name: label }) : "";
}

function updateHistoryNavButton(button, entry, messageKey) {
  if (!button) {
    return;
  }
  const hasTarget = Boolean(entry?.key);
  button.hidden = !hasTarget;
  button.disabled = !hasTarget;
  const label = historyTargetText(entry, messageKey);
  setOptionalAttribute(button, "data-fishy-tooltip", label);
  setOptionalAttribute(button, "aria-label", label);
}

function selectionDataRefreshKey(projection) {
  const selection = projection?.runtime?.selection || null;
  const zoneRgb = zoneRgbFromSelection(selection);
  return JSON.stringify({
    zoneRgb: Number.isInteger(zoneRgb) && zoneRgb >= 0 ? zoneRgb : null,
  });
}

function pointSampleZoneMarkup(row) {
  const zones = Array.isArray(row?.zones) ? row.zones : [];
  if (!zones.length) {
    return "";
  }
  return `
    <div class="fishymap-point-sample-zones">
      ${spriteIcon("hover-zone", "size-4")}
      <span class="fishymap-point-sample-zone-list">
        ${zones
          .map((zone) => `
            <span class="fishymap-point-sample-zone">
              ${pointSampleZoneIndicatorMarkup(zone)}
              <span class="truncate">${escapeHtml(zone?.name || "")}</span>
            </span>
          `)
          .join("")}
      </span>
    </div>
  `;
}

function pointSampleMarkup(row) {
  const name = trimString(row?.fishName) || mapText("info.fish.unknown");
  const gradeTone = itemGradeTone(row?.grade, row?.isPrize === true);
  const toneClass = `fishy-item-grade-${escapeHtml(gradeTone)}`;
  const iconUrl = trimString(row?.iconUrl);
  const itemId = Number.parseInt(row?.itemId, 10);
  const fishId = Number.parseInt(row?.fishId, 10);
  const count = Math.max(1, Number.parseInt(row?.sampleCount, 10) || 1);
  const sampleId = Number.parseInt(row?.sampleId, 10);
  const sampleBadge = count > 1
    ? `x${count}`
    : Number.isInteger(sampleId) && sampleId > 0
      ? `#${sampleId}`
      : "";
  const detailParts = [
    Number.isInteger(itemId) ? `Item ${itemId}` : "",
    Number.isInteger(fishId) ? `Fish ${fishId}` : "",
  ].filter(Boolean);
  const iconMarkup = iconUrl
    ? `<span class="fishy-item-icon-frame is-native ${toneClass}"><img class="fishy-item-icon" src="${escapeHtml(iconUrl)}" alt="${escapeHtml(name)}" crossorigin="anonymous" loading="lazy" decoding="async"></span>`
    : `<span class="fishy-item-icon-frame is-native ${toneClass}"><span class="fishy-item-icon-fallback ${toneClass}">${escapeHtml(name.charAt(0).toUpperCase() || "?")}</span></span>`;
  return `
    <div class="fishymap-point-sample-card" data-zone-kind="${escapeHtml(row?.zoneKind || "")}">
      <div class="fishymap-point-sample-main">
        <span class="fishy-item-row min-w-0">
          ${iconMarkup}
          <span class="fishymap-point-sample-fish min-w-0">
            <span class="fishymap-point-sample-name truncate">${escapeHtml(name)}</span>
            ${
              detailParts.length
                ? `<span class="fishymap-point-sample-ids truncate">${escapeHtml(detailParts.join(" / "))}</span>`
                : ""
            }
          </span>
        </span>
        ${sampleBadge ? `<span class="badge badge-soft badge-sm">${escapeHtml(sampleBadge)}</span>` : ""}
      </div>
      ${
        trimString(row?.timestampText)
          ? `<div class="fishymap-point-sample-timestamp">${spriteIcon("date-confirmed", "size-4")}<span>${escapeHtml(row.timestampText)}</span></div>`
          : ""
      }
      ${pointSampleZoneMarkup(row)}
    </div>
  `;
}

function zoneLootMetricTone(entry) {
  return {
    fillColor: trimString(entry?.fillColor) || "var(--color-base-200)",
    strokeColor: trimString(entry?.strokeColor) || "var(--color-base-300)",
    textColor: trimString(entry?.textColor) || "var(--color-base-content)",
  };
}

function zoneLootClaimCard(claim, entry) {
  const groupLabel = trimString(entry?.groupLabel) || trimString(entry?.label);
  const rows = [{
    label: calculatorDataText("calculator.data.metadata.value", "Value"),
    value: calculatorLootProvenanceAuthoredValueText(claim.value, {
      locale: calculatorDataLocale(),
      path: "zoneLoot.provenance.value",
    }),
    icon: "",
    tone: "",
  }];
  if (claim.sourceDate !== null) {
    rows.push({
      label: claim.sourceDate.kind === "claimDate"
        ? calculatorDataText("calculator.data.metadata.claim_date", "Claim date")
        : calculatorDataText("calculator.data.metadata.imported", "Imported"),
      value: claim.sourceDate.kind === "claimDate"
        ? presentCalculatorClaimDateV1(claim.sourceDate.value)
        : claim.sourceDate.value,
      icon: "",
      tone: "",
    });
  }
  const conditionsText = calculatorLootProvenanceConditionSetText(
    claim.conditions,
    "zoneLoot.provenance.conditions",
  );
  if (conditionsText !== null) {
    rows.push({
      label: calculatorDataText("calculator.data.metadata.conditions", "Conditions"),
      value: conditionsText,
      icon: "",
      tone: "",
    });
  }
  if (claim.comment !== null) {
    rows.push({
      label: calculatorDataText("calculator.data.metadata.comment", "Comment"),
      value: claim.comment,
      icon: "",
      tone: "",
    });
  }
  if (claim.confidence !== null) {
    rows.push({
      label: calculatorDataText("calculator.data.metadata.confidence", "Confidence"),
      value: calculatorDataEnumLabel(claim.confidence),
      icon: "",
      tone: "",
    });
  }
  if (claim.observationCount !== null) {
    rows.push({
      label: calculatorDataText(
        "calculator.data.metadata.observation_count",
        "Observation count",
      ),
      value: String(claim.observationCount),
      icon: claim.sourceIcon === "none" ? "" : claim.sourceIcon,
      tone: claim.sourceTone,
    });
  }
  return Object.freeze({
    laneId: claim.traceId,
    status: claim.status,
    card: Object.freeze({
      summary: `${calculatorLootProvenanceFactLabel(claim.factKind, {
        groupLabel,
        path: "zoneLoot.provenance",
      })} · ${calculatorDataEnumLabel(claim.status)}`,
      badge: claim.sourceName,
      icon: claim.sourceIcon === "none" ? "" : claim.sourceIcon,
      tone: claim.sourceTone,
      rows: Object.freeze(rows.map(Object.freeze)),
    }),
  });
}

function zoneLootProvenanceSegment(entry, channel, domainClaims) {
  const claims = Object.freeze(domainClaims.map((claim) => zoneLootClaimCard(claim, entry)));
  const effectiveClaims = domainClaims.filter(({ status }) => status === "effective");
  const colorClaims = channel === "rate"
    ? effectiveClaims.filter(({ factKind }) => factKind !== "itemQuantity")
    : effectiveClaims;
  const effectiveColorClaims = colorClaims.length > 0 ? colorClaims : effectiveClaims;
  const tones = [...new Set(effectiveColorClaims.map(({ sourceTone }) => sourceTone))];
  const sourceKinds = [...new Set(effectiveColorClaims.map(({ sourceKind }) => sourceKind))];
  const sourceNames = [...new Set(effectiveColorClaims.map(({ sourceName }) => sourceName))];
  const tone = tones.length === 1 ? tones[0] : (tones.length > 1 ? "mixed" : "neutral");
  const label = channel === "presence"
    ? calculatorDataText("common.provenance.channel.presence", "Presence")
    : calculatorDataText("common.provenance.channel.rate", "Rate");
  const sourceLabel = sourceNames.length === 1
    ? sourceNames[0]
    : calculatorDataText("common.provenance.source.mixed_sources", "Mixed sources");
  return Object.freeze({
    active: effectiveClaims.length > 0,
    channel,
    claims,
    color: provenanceIndicatorColorForTone(tone, { active: effectiveClaims.length > 0 }),
    detail: provenanceClaimsText(claims),
    label,
    sourceIcon: effectiveColorClaims.length === 1
      ? (effectiveColorClaims[0].sourceIcon === "none" ? "" : effectiveColorClaims[0].sourceIcon)
      : "",
    sourceKind: sourceKinds.length === 1 ? sourceKinds[0] : "mixed",
    sourceLabel,
    sourceTone: tone,
  });
}

export function zoneLootProvenanceSegments(entry) {
  return [
    ["presence", entry?.presenceClaims],
    ["rate", entry?.rateClaims],
  ].flatMap(([channel, claims]) => (
    Array.isArray(claims) && claims.length > 0
      ? [zoneLootProvenanceSegment(entry, channel, claims)]
      : []
  ));
}

function zoneLootMetricPresentation(
  entry,
  control = null,
  { compactNonKnown = false } = {},
) {
  const metric = zoneLootMetricTone(entry);
  const rateText = trimString(entry?.dropRateText) || "—";
  const rateTitle = trimString(entry?.dropRateTitle);
  const rateState = entry?.dropRateState ?? "unknown";
  return {
    primary: {
      text: rateText,
      unitMark: calculatorLootRateBasisPresentation(entry?.dropRateBasis).unitMark,
      ...(compactNonKnown ? { state: rateState } : {}),
    },
    secondary: null,
    tone: {
      fillColor: metric.fillColor,
      strokeColor: metric.strokeColor,
      textColor: metric.textColor,
    },
    control: control ?? {
      type: "static",
      ariaLabel: `${trimString(entry?.label) || mapText("info.fish.unknown")}: ${rateTitle || rateText}`,
    },
  };
}

function zoneLootRowMarkup(entry) {
  const label = trimString(entry?.label) || mapText("info.fish.unknown");
  return lootItemRowMarkup({
    rowId: `map:${Number.parseInt(entry?.slotIdx, 10) || 0}:${Number.parseInt(entry?.itemId, 10) || 0}`,
    item: {
      label,
      iconUrl: trimString(entry?.iconUrl) || null,
      iconGradeTone: trimString(entry?.iconGradeTone) || "unknown",
      priceText: trimString(entry?.priceText) || null,
      priceLabel: siteText("calculator.server.loot.breakdown.label.base_price"),
      quantityText: null,
      quantityLabel: null,
      control: {
        type: "static",
        ariaLabel: label,
      },
    },
    leadingMetric: zoneLootMetricPresentation(
      entry,
      null,
      { compactNonKnown: true },
    ),
    trailingMetric: null,
    provenanceSegments: zoneLootProvenanceSegments(entry),
    provenanceLabel: mapText("info.provenance"),
  });
}

function zoneLootGroupHeaderMarkup(group) {
  const provenanceRail = provenanceRailMarkup(zoneLootProvenanceSegments(group), {
    label: mapText("info.provenance"),
  });
  const conditionText = trimString(group?.conditionText);
  const conditionTooltip = trimString(group?.conditionTooltip);
  const conditionOptions = Array.isArray(group?.conditionOptions) ? group.conditionOptions : [];
  const conditionOptionIndex = Number.parseInt(group?.conditionOptionIndex, 10);
  const selectedIndex = Number.isInteger(conditionOptionIndex)
    && conditionOptionIndex >= 0
    && conditionOptionIndex < conditionOptions.length
    ? conditionOptionIndex
    : 0;
  const selectionKey = trimString(group?.conditionOptionKey)
    || `map-loot:${Number.parseInt(group?.slotIdx, 10) || 0}:${trimString(group?.label)}`;
  const switcherOptions = conditionOptions.length
    ? conditionOptions.map((option, index) => ({
        id: trimString(option?.conditionId) || `${selectionKey}:${index}`,
        label: trimString(option?.conditionText)
          || calculatorDataText("common.status.default", "Default"),
        title: trimString(option?.conditionTooltip),
      }))
    : conditionText
      ? [{ id: `${selectionKey}:current`, label: conditionText, title: conditionTooltip }]
      : [];
  const conditionMarkup = switcherOptions.length
    ? lootConditionSwitcherMarkup({
        selectionKey,
        selectedIndex,
        options: switcherOptions,
        previousLabel: mapText("info.zone_loot.condition.previous"),
        nextLabel: mapText("info.zone_loot.condition.next"),
        unselectedLabel: calculatorDataText(
          "calculator.data.unavailable.model_selection",
          "Choose condition",
        ),
        disabled: false,
      })
    : "";
  return `
    <div class="fishymap-zone-loot-group-header">
      <div class="fishymap-zone-loot-group-heading">
        <span class="badge badge-soft badge-sm">${escapeHtml(group.label)}</span>
        ${conditionMarkup}
      </div>
      <div class="fishymap-zone-loot-group-rate">
        ${renderLootMetricCell(zoneLootMetricPresentation(
          group,
          null,
          { compactNonKnown: true },
        ))}
        ${provenanceRail}
      </div>
    </div>
  `;
}

function zoneLootGroupCollectionMarkup(groups) {
  const groupMarkup = groups.length
    ? groups
        .map((group) => `
          <div
            class="fishymap-zone-loot-group rounded-box border border-base-300 bg-base-200/75 p-2"
            data-zone-loot-group-slot="${escapeHtml(Number.parseInt(group?.slotIdx, 10) || 0)}"
          >
            ${zoneLootGroupHeaderMarkup(group)}
            <div class="fishymap-zone-loot-group-rows">
              ${
                group.rows.length
                  ? group.rows.map((row) => zoneLootRowMarkup(row)).join("")
                  : `<div class="px-2 py-2 text-[11px] text-base-content/55">${escapeHtml(mapText("info.empty_group"))}</div>`
              }
            </div>
          </div>
        `)
        .join("")
    : `<div class="px-2 py-3 text-xs text-base-content/60">${escapeHtml(mapText("info.empty_zone"))}</div>`;
  return `<div class="fishymap-zone-loot-groups">${groupMarkup}</div>`;
}

function zoneLootProfileMarkup(profile) {
  const groups = Array.isArray(profile?.groups) ? profile.groups : [];
  return `
    <div class="fishymap-zone-loot-profile rounded-box border border-base-300/85 bg-base-100/75 p-3">
      <div class="fishymap-zone-loot-profile-header">
        <span class="badge badge-outline badge-sm">${escapeHtml(profile?.label || "")}</span>
      </div>
      ${zoneLootGroupCollectionMarkup(groups)}
    </div>
  `;
}

function zoneLootProfileTabsMarkup(section) {
  const profiles = Array.isArray(section?.profiles) ? section.profiles : [];
  const activeProfileId = trimString(section?.activeProfileId);
  const activeProfile = profiles.find((profile) => profile?.id === activeProfileId)
    || section?.activeProfile
    || profiles[0]
    || null;
  if (!profiles.length) {
    return zoneLootGroupCollectionMarkup(Array.isArray(section?.groups) ? section.groups : []);
  }
  const tabs = profiles.map((profile) => {
    const profileId = trimString(profile?.id);
    const active = profileId === activeProfile?.id;
    return `
      <button
        class="tab gap-2 ${active ? "tab-active" : ""}"
        type="button"
        role="tab"
        aria-selected="${active ? "true" : "false"}"
        data-zone-loot-profile="${escapeHtml(profileId)}"
      >
        ${spriteIcon(profile?.icon || "fish-fill", "size-4")}
        <span>${escapeHtml(profile?.label || profileId)}</span>
      </button>
    `;
  }).join("");
  return `
    <div class="space-y-2">
      <div class="tabs tabs-box bg-base-200/80 p-1" role="tablist" aria-label="${escapeHtml(mapText("info.zone_loot.profile_tabs"))}">
        ${tabs}
      </div>
      ${zoneLootGroupCollectionMarkup(Array.isArray(activeProfile?.groups) ? activeProfile.groups : [])}
    </div>
  `;
}

function zoneLootNoticeToneStyles(tone = "info") {
  if (tone === "warning") {
    return {
      card:
        "border-color: color-mix(in oklab, var(--color-warning, #c77d19) 56%, var(--color-base-300, #d4d4d8) 44%); background: color-mix(in oklab, var(--color-warning, #c77d19) 14%, var(--color-base-100, #ffffff) 86%);",
      icon: "color: var(--color-warning, #f59e0b);",
      title:
        "color: color-mix(in oklab, var(--color-warning, #c77d19) 78%, var(--color-base-content, #1f2937) 22%);",
    };
  }
  return {
    card:
      "border-color: color-mix(in oklab, var(--color-info, #0ea5e9) 42%, var(--color-base-300, #d4d4d8) 58%); background: color-mix(in oklab, var(--color-info, #0ea5e9) 10%, var(--color-base-100, #ffffff) 90%);",
    icon: "color: var(--color-info, #0ea5e9);",
    title:
      "color: color-mix(in oklab, var(--color-info, #0ea5e9) 72%, var(--color-base-content, #1f2937) 28%);",
  };
}

function zoneLootNoticeCardMarkup({
  tone = "info",
  iconName = "information-circle",
  title = "",
  paragraphs = [],
} = {}) {
  const content = Array.from(
    new Set(
      (Array.isArray(paragraphs) ? paragraphs : [])
        .map((paragraph) => trimString(paragraph))
        .filter(Boolean),
    ),
  );
  const heading = trimString(title);
  if (!heading && !content.length) {
    return "";
  }
  const styles = zoneLootNoticeToneStyles(tone);
  return `
    <div class="rounded-box border px-4 py-4" style="${styles.card}">
      <div class="flex items-start gap-3">
        <div class="shrink-0 pt-0.5" style="${styles.icon}">
          ${spriteIcon(iconName, "size-6")}
        </div>
        <div class="min-w-0">
          ${
            heading
              ? `<div class="text-sm font-semibold uppercase tracking-widest" style="${styles.title}">${escapeHtml(heading)}</div>`
              : ""
          }
          ${
            content.length
              ? `<div class="mt-2 space-y-2 text-sm leading-relaxed text-base-content/85">${content.map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`).join("")}</div>`
              : ""
          }
        </div>
      </div>
    </div>
  `;
}

function zoneLootDataQualityWarningMarkup(section) {
  if (section?.available !== true) {
    return "";
  }
  return zoneLootNoticeCardMarkup({
    tone: "warning",
    iconName: "alert-fill",
    title: siteText("calculator.server.disclaimer.title"),
    paragraphs: [
      siteText("calculator.server.disclaimer.p1"),
      siteText("calculator.server.disclaimer.p2"),
      siteText("calculator.server.disclaimer.p3"),
      siteText("calculator.server.disclaimer.p4"),
      siteText("calculator.server.disclaimer.p5"),
    ],
  });
}

function zoneLootStatusNoticeMarkup(section) {
  if (section?.available === true) {
    return "";
  }
  return zoneLootNoticeCardMarkup({
    tone: "info",
    iconName: "information-circle",
    title: mapText("info.zone_loot.notice.status_title"),
    paragraphs: [trimString(section?.note)],
  });
}

function zoneLootNoticeDisclosureMarkup(section) {
  const notices = [
    zoneLootDataQualityWarningMarkup(section),
    zoneLootStatusNoticeMarkup(section),
  ].filter(Boolean);
  if (!notices.length) {
    return "";
  }
  return `
    <fishy-notice-disclosure
      summary-label="${escapeHtml(mapText("info.notice.title"))}"
      icon="alert-triangle"
      notice-id="map-zone-pane"
      body-class="space-y-3 px-1 pb-1 pt-3"
      open
    >
      ${notices.join("")}
    </fishy-notice-disclosure>
  `;
}

function zoneLootSectionMarkup(section) {
  return `
    <section class="space-y-2">
      <div class="flex items-center justify-between gap-3">
        <p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section.title || mapText("info.section.fish"))}</p>
        <span class="text-[11px] text-base-content/55">${escapeHtml(section.statusText || mapText("info.zone_loot.status.idle"))}</span>
      </div>
      ${zoneLootNoticeDisclosureMarkup(section)}
      <div class="fishymap-zone-loot-profiles">${zoneLootProfileTabsMarkup(section)}</div>
    </section>
  `;
}

function pointSamplePageCount(rows, pageSize = POINT_SAMPLE_PAGE_SIZE) {
  return Math.max(1, Math.ceil(rows.length / pageSize));
}

function normalizePointSamplePage(value, rows, pageSize = POINT_SAMPLE_PAGE_SIZE) {
  const page = Number.parseInt(value, 10);
  if (!Number.isInteger(page) || page < 0) {
    return 0;
  }
  return Math.min(page, pointSamplePageCount(rows, pageSize) - 1);
}

function pointSampleSectionMarkup(section, { pointSamplePage = 0 } = {}) {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  if (!rows.length) {
    return "";
  }
  const page = normalizePointSamplePage(pointSamplePage, rows);
  const pageCount = pointSamplePageCount(rows);
  const start = page * POINT_SAMPLE_PAGE_SIZE;
  const end = Math.min(start + POINT_SAMPLE_PAGE_SIZE, rows.length);
  const visibleRows = rows.slice(start, end);
  const hasPages = rows.length > POINT_SAMPLE_PAGE_SIZE;
  const sampleRangeText = hasPages
    ? `Showing ${start + 1}-${end} of ${rows.length} samples`
    : `${rows.length} sample${rows.length === 1 ? "" : "s"}`;
  const pagingMarkup = hasPages
    ? `
      <div class="fishymap-point-sample-pager">
        <button
          class="btn btn-ghost btn-xs"
          type="button"
          data-point-sample-page="${Math.max(0, page - 1)}"
          ${page <= 0 ? "disabled" : ""}
        >Prev</button>
        <span class="text-[11px] text-base-content/55">Page ${page + 1}/${pageCount}</span>
        <button
          class="btn btn-ghost btn-xs"
          type="button"
          data-point-sample-page="${Math.min(pageCount - 1, page + 1)}"
          ${page >= pageCount - 1 ? "disabled" : ""}
        >Next</button>
      </div>
    `
    : "";
  return `
    <section class="space-y-2">
      ${
        trimString(section?.title)
          ? `<p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section.title)}</p>`
          : ""
      }
      <div class="fishymap-point-sample-summary">
        <span>${escapeHtml(sampleRangeText)}</span>
        ${hasPages ? `<span>Sorted by occurrence</span>` : ""}
      </div>
      ${pagingMarkup}
      <div class="fishymap-point-sample-list">${visibleRows.map((row) => pointSampleMarkup(row)).join("")}</div>
      ${pagingMarkup}
    </section>
  `;
}

function hotspotStatMarkup(metric) {
  const icon = trimString(metric?.icon);
  const iconMarkup = icon
    ? `
      <div class="stat-figure text-base-content/45">
        ${spriteIcon(icon, "size-5")}
      </div>
    `
    : "";
  return `
    <div class="stat min-w-0 px-3 py-3">
      ${iconMarkup}
      <div class="stat-title text-[11px] font-semibold uppercase tracking-[0.16em] text-base-content/45">${escapeHtml(metric?.label || "")}</div>
      <div class="stat-value truncate text-base font-semibold text-base-content">${escapeHtml(metric?.value || "")}</div>
    </div>
  `;
}

function hotspotStatsRowMarkup(metrics) {
  const visibleMetrics = metrics.filter((metric) => trimString(metric?.value));
  if (!visibleMetrics.length) {
    return "";
  }
  return `
    <div class="stats stats-vertical min-w-0 border border-base-300/75 bg-base-100/75 shadow-none sm:stats-horizontal">
      ${visibleMetrics.map((metric) => hotspotStatMarkup(metric)).join("")}
    </div>
  `;
}

function hotspotStatsMarkup(section) {
  const metrics = Array.isArray(section?.metrics) ? section.metrics : [];
  const timingMetrics = [
    {
      label: "Bite Min",
      value: trimString(section?.biteTime?.minimum),
      icon: "stopwatch",
    },
    {
      label: "Bite Avg",
      value: trimString(section?.biteTime?.average),
      icon: "stopwatch",
    },
    {
      label: "Bite Max",
      value: trimString(section?.biteTime?.maximum),
      icon: "stopwatch",
    },
  ];
  const catchMetrics = [
    ...metrics.slice(0, 2),
    {
      label: "Lifetime",
      value: trimString(section?.lifetime),
      icon: "time-fill",
    },
  ];
  const rows = [timingMetrics, catchMetrics, metrics.slice(2, 4)]
    .map((row) => row.filter((metric) => trimString(metric?.value)))
    .filter((row) => row.length > 0);
  if (!rows.length) {
    return "";
  }
  return `
    <div class="grid gap-2">
      ${rows.map((row) => hotspotStatsRowMarkup(row)).join("")}
    </div>
  `;
}

function hotspotModelViewerMarkup(modelViewer, options = {}) {
  if (options.threeDEnabled === false) {
    return "";
  }
  const activeKey = trimString(modelViewer?.activeKey);
  const hotspotAction = {
    label: trimString(modelViewer?.hotspotAction?.label),
    value: trimString(modelViewer?.hotspotAction?.value),
    icon: trimString(modelViewer?.hotspotAction?.icon),
  };
  const assets = (Array.isArray(modelViewer?.assets) ? modelViewer.assets : [])
    .map((asset) => {
      const normalized = {
        key: trimString(asset?.key),
        kind: trimString(asset?.kind),
        label: trimString(asset?.label),
        src: trimString(asset?.src),
      };
      if (asset?.animated === false) {
        normalized.animated = false;
      }
      for (const key of [
        "shadowIntensity",
        "environmentImage",
        "exposure",
        "cameraOrbit",
        "cameraTarget",
        "fieldOfView",
      ]) {
        const value = trimString(asset?.[key]);
        if (value) {
          normalized[key] = value;
        }
      }
      return normalized;
    })
    .filter((asset) => asset.src);
  if (!assets.length) {
    return "";
  }
  const actionAttrs = hotspotAction.value
    ? [
        `data-action-label="${escapeHtml(hotspotAction.label || "Hotspot Action")}"`,
        `data-action-value="${escapeHtml(hotspotAction.value)}"`,
        hotspotAction.icon ? `data-action-icon="${escapeHtml(hotspotAction.icon)}"` : "",
      ].filter(Boolean).join(" ")
    : "";
  return `<fishymap-hotspot-model-viewer data-assets="${jsonAttribute(assets)}"${activeKey ? ` data-active-key="${escapeHtml(activeKey)}"` : ""}${actionAttrs ? ` ${actionAttrs}` : ""}></fishymap-hotspot-model-viewer>`;
}

function hotspotSectionMarkup(section, options = {}) {
  const groups = Array.isArray(section?.groups) ? section.groups : [];
  const profiles = Array.isArray(section?.profiles) ? section.profiles : [];
  const modelFacts = options.threeDEnabled === false || section?.modelViewer?.hotspotAction?.value
    ? []
    : Array.isArray(section?.modelFacts)
      ? section.modelFacts
      : [];
  const profileMarkup = profiles.length
    ? profiles.map((profile) => zoneLootProfileMarkup(profile)).join("")
    : zoneLootGroupCollectionMarkup(groups);
  return `
    <section class="space-y-3">
      ${
        trimString(section?.title)
          ? `<p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section.title)}</p>`
          : ""
      }
      ${hotspotStatsMarkup(section)}
      ${
        modelFacts.length
          ? `<div class="fishymap-overview-list">${modelFacts.map((fact) => factMarkup(fact)).join("")}</div>`
          : ""
      }
      ${
        groups.length || profiles.length
          ? `<div class="fishymap-zone-loot-profiles">${profileMarkup}</div>`
          : ""
      }
      ${hotspotModelViewerMarkup(section?.modelViewer, options)}
    </section>
  `;
}

function tradeNpcSectionMarkup(section) {
  const npcName = trimString(section?.npcName) || "NPC";
  const imageUrl = trimString(section?.imageUrl);
  const regionLabel = trimString(section?.regionLabel);
  const npcKey = Number.isInteger(section?.npcKey) && section.npcKey > 0 ? section.npcKey : null;
  const imageMarkup = tradeNpcPortraitMarkup({
    imageUrl,
    badgeImageUrl: section?.badgeImageUrl,
    name: npcName,
    variant: "detail",
  });
  return `
    <section class="space-y-3">
      <div class="fishymap-trade-npc-profile rounded-box flex min-w-0 items-center gap-4 border border-base-300/80 bg-base-100/80 p-3 shadow-xs">
        ${imageMarkup}
        <div class="min-w-0 flex-1">
          <h3 class="m-0 break-words text-base font-semibold text-base-content">${escapeHtml(npcName)}</h3>
          ${regionLabel ? `<p class="m-0 mt-1 break-words text-xs text-base-content/60">${escapeHtml(regionLabel)}</p>` : ""}
          ${npcKey == null ? "" : `<p class="m-0 mt-1 text-[11px] text-base-content/45">NPC ID ${escapeHtml(npcKey)}</p>`}
        </div>
      </div>
    </section>
  `;
}

function sectionMarkup(section, options = {}) {
  switch (trimString(section?.kind)) {
    case "point-samples":
      return pointSampleSectionMarkup(section, options);
    case "hotspot":
      return hotspotSectionMarkup(section, options);
    case "trade-npc":
      return tradeNpcSectionMarkup(section);
    case "trade-managers":
      return tradeManagerSectionMarkup(section, options);
    case "character-activity":
      return `<div data-fishymap-character-activity-mount data-character-identity-key="${escapeHtml(section?.identityKey || "")}"></div>`;
    case "facts":
      return `
        <section class="space-y-2">
          ${
            trimString(section?.title)
              ? `<p class="text-[11px] font-semibold uppercase tracking-[0.18em] text-base-content/45">${escapeHtml(section.title)}</p>`
              : ""
          }
          <div class="fishymap-overview-list">${(Array.isArray(section?.facts) ? section.facts : [])
            .map((fact) => factMarkup(fact))
            .join("")}</div>
        </section>
      `;
    case "location-bookmark":
      return locationBookmarkSectionMarkup(section);
    case "zone-loot":
      return zoneLootSectionMarkup(section);
    case "zone-mask-lineage":
      return zoneMaskLineageSectionMarkup(section, escapeHtml);
    default:
      return "";
  }
}

function emptyPanelMarkup() {
  return `
    <section class="space-y-2 rounded-box border border-base-300/70 bg-base-200 px-3 py-3">
      <h2 class="m-0 text-base font-semibold text-base-content">${escapeHtml(mapText("info.empty_about_title"))}</h2>
      <p class="m-0 text-sm text-base-content/75">${escapeHtml(mapText("info.empty_about"))}</p>
      <p class="m-0 text-sm text-base-content/60">${escapeHtml(mapText("info.empty_selection"))}</p>
      <a class="link link-hover inline-flex items-center gap-1.5 text-sm font-medium text-primary" href="/glossary/">
        ${spriteIcon("book-open", "size-4")}
        <span>${escapeHtml(siteText("nav.glossary"))}</span>
      </a>
    </section>
  `;
}

function pointSampleSectionKey(section) {
  const rows = Array.isArray(section?.rows) ? section.rows : [];
  if (!rows.length) {
    return "";
  }
  return [
    trimString(section?.id),
    rows.length,
    trimString(rows[0]?.key),
    trimString(rows[rows.length - 1]?.key),
  ].join(":");
}

function panelRenderKey(viewModel, pointSamplePage, options = {}) {
  const sections = viewModel.activePane?.sections || [];
  const threeDEnabled = options.threeDEnabled !== false;
  return JSON.stringify({
    empty: viewModel.empty,
    emptyCopy: viewModel.empty
      ? [
          mapText("info.empty_about_title"),
          mapText("info.empty_about"),
          mapText("info.empty_selection"),
          siteText("nav.glossary"),
        ]
      : null,
    activePaneId: viewModel.activePaneId,
    pointSamplePage,
    threeDEnabled,
    tradeManagerFilter: normalizeTradeManagerFilter(options.tradeManagerFilter),
    sections: sections.map((section) => {
      if (!threeDEnabled && trimString(section?.kind) === "hotspot") {
        const { modelFacts: _modelFacts, modelViewer: _modelViewer, ...safeSection } = section || {};
        return safeSection;
      }
      if (trimString(section?.kind) !== "point-samples") {
        return section;
      }
      const rows = Array.isArray(section?.rows) ? section.rows : [];
      const page = normalizePointSamplePage(pointSamplePage, rows);
      const start = page * POINT_SAMPLE_PAGE_SIZE;
      const end = Math.min(start + POINT_SAMPLE_PAGE_SIZE, rows.length);
      return {
        id: section.id,
        kind: section.kind,
        title: section.title,
        rowCount: rows.length,
        page,
        visibleRowKeys: rows.slice(start, end).map((row) => row?.key || ""),
      };
    }),
  });
}

export function catchProfileStateForInfoViewModel(viewModel, zoneLootStatus) {
  const zonePane = Array.isArray(viewModel?.panes)
    ? viewModel.panes.find((pane) => pane?.id === "zone")
    : null;
  const catchProfile = Array.isArray(zonePane?.sections)
    ? zonePane.sections.find((section) => section?.kind === "zone-loot")
    : null;
  if (catchProfile?.available === true) {
    return "available";
  }
  return zoneLootStatus === "loading" ? "loading" : "unavailable";
}

function ensureInfoPanelMarkup(host) {
  if (host.querySelector("#fishymap-zone-info-tabs")) {
    return;
  }
  host.innerHTML = `
    <div
      id="fishymap-zone-info-tabs"
      role="tablist"
      class="tabs tabs-box bg-base-200/80 p-1"
      aria-label="${escapeHtml(mapText("info.tabs"))}"
      hidden
    ></div>
    <div id="fishymap-zone-info-panel" class="space-y-3">
      ${emptyPanelMarkup()}
    </div>
  `;
}

export class FishyMapInfoPanelElement extends HTMLElementBase {
  static get observedAttributes() {
    return [
      "data-loot-rate-display-mode",
      "data-three-d-enabled",
      "data-zone-loot-sort-by",
      "data-zone-loot-sort-order",
    ];
  }

  constructor() {
    super();
    this._shell = null;
    this._mapShellProjection = null;
    this._calculatorMapContextProjection = null;
    this._zoneLootAbortController = null;
    this._loadZoneLootSummary = loadZoneLootSummary;
    this._copyLocationBookmark = copyLocationBookmark;
    this._renderQueued = false;
    this._selectionDataRefreshQueued = false;
    this._elements = null;
    this._state = {
      zoneCatalog: [],
      zoneLootStatus: "idle",
      zoneLootSummary: null,
      zoneLootRgb: null,
      zoneLootRequestToken: 0,
      zoneLootConditionSelection: {},
      pointSamplePage: 0,
      pointSampleSectionKey: "",
      tradeNpcMapCatalog: null,
      tradeNpcMapStatus: "idle",
      tradeManagerFilter: TRADE_MANAGER_FILTER_ALL,
      selectionDataRefreshKey: null,
      selectionHistoryEntries: [],
      selectionHistoryIndex: -1,
      pendingHistoryRestore: null,
      characterActivityIdentityKey: "",
    };
    this._handleSelectionChanged = (event) => {
      this.handleSelectionChanged(event?.detail || null);
    };
    this._handleV2EventBatch = (event) => {
      const events = Array.isArray(event?.detail?.events) ? event.detail.events : [];
      for (const entry of events) {
        if (entry?.type === "selection-changed") {
          this._handleSelectionChanged({ detail: { ...entry, state: { selection: entry } } });
        }
      }
    };
    this._handleZoneCatalogReady = (event) => {
      this._state.zoneCatalog = Array.isArray(event?.detail?.zoneCatalog)
        ? cloneJson(event.detail.zoneCatalog)
        : [];
      this.scheduleRender();
      void this.refreshZoneLootSummary();
    };
    this._handleTradeNpcCatalogChanged = (event) => {
      this._state.tradeNpcMapCatalog = event?.detail?.catalog
        ? cloneJson(event.detail.catalog)
        : null;
      this._state.tradeNpcMapStatus = trimString(event?.detail?.status) || "idle";
      this.scheduleRender();
    };
    this._handleLanguageChanged = () => {
      this.scheduleRender();
      void this.refreshZoneLootSummary({ force: true });
    };
    this._handleHistoryBackClick = (event) => {
      event.preventDefault?.();
      event.stopPropagation?.();
      this.restoreSelectionHistoryEntry("back");
    };
    this._handleHistoryForwardClick = (event) => {
      event.preventDefault?.();
      event.stopPropagation?.();
      this.restoreSelectionHistoryEntry("forward");
    };
    this._handleStatusCopyClick = (event) => {
      event.preventDefault?.();
      this.copyCurrentSelectionBookmark();
    };
    this._handleClick = (event) => {
      const copyLocationButton = event.target.closest(
        "button[data-fishy-copy-location-bookmark]",
      );
      if (copyLocationButton) {
        event.preventDefault?.();
        this.copyCurrentSelectionBookmark();
        return;
      }

      const createLocationButton = event.target.closest(
        "button[data-fishy-create-location-bookmark]",
      );
      if (createLocationButton) {
        event.preventDefault?.();
        const bookmark = this.createCurrentSelectionBookmark();
        globalThis.__fishystuffToast?.[bookmark ? "success" : "warning"]?.(
          mapText(
            bookmark
              ? "bookmarks.toast.created_single"
              : "info.location.bookmark_unavailable",
          ),
        );
        return;
      }

      const tradeManagerFilter = event.target.closest("button[data-trade-manager-filter]");
      if (tradeManagerFilter) {
        const nextFilter = normalizeTradeManagerFilter(
          tradeManagerFilter.getAttribute("data-trade-manager-filter"),
        );
        if (nextFilter !== this._state.tradeManagerFilter) {
          this._state.tradeManagerFilter = nextFilter;
          this.scheduleRender();
        }
        return;
      }

      const focusButton = event.target.closest("button[data-fishy-focus-world-point]");
      if (focusButton) {
        event.preventDefault?.();
        const focusWorldPoint = focusWorldPointFromButton(focusButton);
        const target = focusWorldPointRequestTarget(focusWorldPoint);
        if (target) {
          dispatchFocusWorldPointRequest(componentEventTarget(this), target);
        }
        return;
      }

      const lootProfileButton = event.target.closest("button[data-zone-loot-profile]");
      if (lootProfileButton) {
        event.preventDefault?.();
        const lootProfile = trimString(
          lootProfileButton.getAttribute("data-zone-loot-profile"),
        ).toLowerCase();
        if (["rod", "harpoon", "unassigned"].includes(lootProfile)) {
          dispatchZoneLootProfileChange(componentEventTarget(this), lootProfile);
        }
        return;
      }

      const conditionButton = event.target.closest("button[data-loot-condition-direction]");
      if (conditionButton) {
        event.preventDefault?.();
        const selection = lootConditionSwitcherActionFromTrigger(conditionButton);
        const nextSelection = {
          ...this.zoneLootConditionSelection(),
          [selection.key]: selection.optionIndex,
        };
        this._state.zoneLootConditionSelection = nextSelection;
        dispatchLootConditionOptionSelect(
          componentEventTarget(this),
          selection.key,
          selection.optionIndex,
        );
        this.scheduleRender();
        return;
      }

      const pointSamplePageButton = event.target.closest("button[data-point-sample-page]");
      if (pointSamplePageButton) {
        event.preventDefault?.();
        this._state.pointSamplePage = Math.max(
          0,
          Number.parseInt(pointSamplePageButton.getAttribute("data-point-sample-page"), 10) || 0,
        );
        this.scheduleRender();
        return;
      }

      const button = event.target.closest("button[data-zone-info-tab]");
      if (!button) {
        return;
      }
      const paneId = trimString(button.getAttribute("data-zone-info-tab"));
      dispatchZoneInfoTabChange(componentEventTarget(this), paneId);
    };
  }

  get mapShellProjection() {
    return this._mapShellProjection;
  }

  get calculatorMapContextProjection() {
    return this._calculatorMapContextProjection;
  }

  set calculatorMapContextProjection(value) {
    const projection = parseCalculatorMapContextProjectionV1(value);
    const previous = this._calculatorMapContextProjection;
    if (previous) {
      if (projection.revision < previous.revision) {
        throw new RangeError("Calculator map context projection revision cannot regress");
      }
      if (projection.revision === previous.revision) {
        return;
      }
    }
    this._calculatorMapContextProjection = projection;
    if (
      !previous
      || projection.state !== previous.state
      || projection.calculator.resolvedRevision
        !== previous.calculator.resolvedRevision
      || projection.overlay.state !== previous.overlay.state
      || projection.overlay.workspaceRevision !== previous.overlay.workspaceRevision
    ) {
      this._cancelZoneLootRequest();
      if (this._shell) {
        void this.refreshZoneLootSummary({ force: true });
      }
    }
  }

  set mapShellProjection(projection) {
    assertMapShellProjectionV1(projection);
    const previousProjection = this._mapShellProjection;
    if (previousProjection) {
      if (projection.revision < previousProjection.revision) {
        throw new RangeError("Map info panel projection revision cannot regress");
      }
      if (projection.revision === previousProjection.revision) {
        return;
      }
    }
    this._mapShellProjection = projection;
    if (!this._elements) {
      return;
    }
    if (
      !previousProjection ||
      projection.ui !== previousProjection.ui ||
      projection.runtime !== previousProjection.runtime ||
      projection.bridged !== previousProjection.bridged
    ) {
      this.scheduleRender();
    }
    if (
      !previousProjection ||
      projection.runtime.selection !== previousProjection.runtime.selection
    ) {
      this.scheduleSelectionDataRefresh();
    }
  }

  attributeChangedCallback(name, previousValue, nextValue) {
    if (
      ![
        "data-loot-rate-display-mode",
        "data-three-d-enabled",
        "data-zone-loot-sort-by",
        "data-zone-loot-sort-order",
      ].includes(name) ||
      previousValue === nextValue
    ) {
      return;
    }
    this.scheduleRender();
  }

  connectedCallback() {
    upgradeProperty(this, "calculatorMapContextProjection");
    this._shell = this.closest?.("#map-page-shell") || null;
    if (
      !this._calculatorMapContextProjection
      && this._shell?.calculatorMapContextProjection
    ) {
      this.calculatorMapContextProjection = this._shell.calculatorMapContextProjection;
    }
    this._state.tradeNpcMapCatalog = this._shell?.fishyMapTradeNpcCatalog
      ? cloneJson(this._shell.fishyMapTradeNpcCatalog)
      : null;
    this._state.tradeNpcMapStatus = trimString(this._shell?.fishyMapTradeNpcCatalogStatus) || "idle";
    ensureInfoPanelMarkup(this);
    this._elements = {
      title: this._shell?.querySelector?.("#fishymap-zone-info-title") || null,
      titleIcon: this._shell?.querySelector?.("#fishymap-zone-info-title-icon") || null,
      historyBack: this._shell?.querySelector?.("#fishymap-zone-info-history-back") || null,
      historyForward: this._shell?.querySelector?.("#fishymap-zone-info-history-forward") || null,
      status: this._shell?.querySelector?.("#fishymap-zone-info-status") || null,
      statusIcon: this._shell?.querySelector?.("#fishymap-zone-info-status-icon") || null,
      statusText: this._shell?.querySelector?.("#fishymap-zone-info-status-text") || null,
      tabs: this.querySelector("#fishymap-zone-info-tabs"),
      panel: this.querySelector("#fishymap-zone-info-panel"),
    };
    this.addEventListener("click", this._handleClick);
    this._shell?.addEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.addEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.addEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    this._elements.historyBack?.addEventListener?.("click", this._handleHistoryBackClick);
    this._elements.historyForward?.addEventListener?.("click", this._handleHistoryForwardClick);
    this._elements.status?.addEventListener?.("click", this._handleStatusCopyClick);
    globalThis.window?.addEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChanged,
    );
    this.render();
    this.scheduleSelectionDataRefresh();
  }

  disconnectedCallback() {
    // A detached component cannot bubble its final ownership release. Route
    // this lifecycle-only transition to the shell explicitly.
    this.selectCharacterActivity("", this._shell);
    this.parkCharacterActivitySurface();
    this._cancelZoneLootRequest();
    this.removeEventListener("click", this._handleClick);
    this._elements?.historyBack?.removeEventListener?.("click", this._handleHistoryBackClick);
    this._elements?.historyForward?.removeEventListener?.("click", this._handleHistoryForwardClick);
    this._elements?.status?.removeEventListener?.("click", this._handleStatusCopyClick);
    this._shell?.removeEventListener?.(FISHYMAP_V2_EVENT, this._handleV2EventBatch);
    this._shell?.removeEventListener?.(FISHYMAP_ZONE_CATALOG_READY_EVENT, this._handleZoneCatalogReady);
    this._shell?.removeEventListener?.(
      FISHYMAP_TRADE_NPC_CATALOG_CHANGED_EVENT,
      this._handleTradeNpcCatalogChanged,
    );
    globalThis.window?.removeEventListener?.(
      globalThis.window?.__fishystuffLanguage?.event || "fishystuff:languagechange",
      this._handleLanguageChanged,
    );
    this._renderQueued = false;
    this._selectionDataRefreshQueued = false;
    this._shell = null;
    this._elements = null;
  }

  infoViewSignals(projection = this._mapShellProjection) {
    return infoViewSignalsFromProjection(projection);
  }

  lootRateDisplayMode(projection = this._mapShellProjection) {
    const signalValue = projection?.ui?.windowUi?.settings?.lootRateDisplayMode;
    const attrValue = this.getAttribute?.("data-loot-rate-display-mode");
    return parseCalculatorLootRateDisplayMode(
      signalValue ?? (trimString(attrValue) || "normalized"),
      "map info loot rate display mode",
    );
  }

  threeDEnabled(projection = this._mapShellProjection) {
    const signalValue = projection?.ui?.windowUi?.settings?.threeDEnabled;
    if (typeof signalValue === "boolean") {
      return signalValue;
    }
    const attrValue = trimString(this.getAttribute?.("data-three-d-enabled")).toLowerCase();
    return attrValue !== "false" && attrValue !== "0";
  }

  zoneLootSortBy(projection = this._mapShellProjection) {
    const signalValue = projection?.ui?.windowUi?.settings?.zoneLootSortBy;
    if (typeof signalValue === "string") {
      return signalValue;
    }
    return trimString(this.getAttribute?.("data-zone-loot-sort-by")) || "calculator";
  }

  zoneLootSortOrder(projection = this._mapShellProjection) {
    const signalValue = projection?.ui?.windowUi?.settings?.zoneLootSortOrder;
    if (typeof signalValue === "string") {
      return signalValue;
    }
    return trimString(this.getAttribute?.("data-zone-loot-sort-order")) || "calculator";
  }

  zoneLootConditionSelection(projection = this._mapShellProjection) {
    const bridged = projection?.bridged?.filters?.lootConditionSelection;
    if (bridged && typeof bridged === "object" && !Array.isArray(bridged)) {
      return cloneJson(bridged);
    }
    return cloneJson(this._state.zoneLootConditionSelection || {});
  }

  characterActivitySurface() {
    return this._shell?.querySelector?.("#fishymap-character-activity-surface") || null;
  }

  parkCharacterActivitySurface() {
    const surface = this.characterActivitySurface();
    const parking = this._shell?.querySelector?.("#fishymap-character-activity-parking") || null;
    if (surface && parking && surface.parentElement !== parking) {
      parking.appendChild?.(surface);
    }
    if (surface) {
      surface.hidden = true;
    }
    return surface;
  }

  mountCharacterActivitySurface(identityKey) {
    const normalizedIdentityKey = trimString(identityKey);
    const surface = this.characterActivitySurface();
    const mount = this._elements?.panel?.querySelector?.(
      "[data-fishymap-character-activity-mount]",
    ) || null;
    if (!surface || !mount || !normalizedIdentityKey) {
      return false;
    }
    setOptionalAttribute(surface, "data-character-identity-key", normalizedIdentityKey);
    surface.hidden = false;
    mount.appendChild?.(surface);
    return true;
  }

  selectCharacterActivity(identityKey, eventTarget = componentEventTarget(this)) {
    const normalizedIdentityKey = trimString(identityKey);
    if (trimString(this._state.characterActivityIdentityKey) === normalizedIdentityKey) {
      return false;
    }
    if (!dispatchCharacterActivityTargetChange(
      eventTarget,
      normalizedIdentityKey,
    )) {
      return false;
    }
    this._state.characterActivityIdentityKey = normalizedIdentityKey;
    return true;
  }

  recordSelectionForHistory(selection, { runtimeLayers = null } = {}) {
    const entry = selectionHistoryEntryFromSelection(selection, {
      zoneCatalog: this._state.zoneCatalog,
      runtimeLayers: Array.isArray(runtimeLayers)
        ? runtimeLayers
        : runtimeLayersFromProjection(this._mapShellProjection),
    });
    if (!entry) {
      return;
    }
    if (entry.historyBehavior === "navigate") {
      const restoreIndex = Number.parseInt(this._state.pendingHistoryRestore?.index, 10);
      if (Number.isInteger(restoreIndex) && restoreIndex >= 0) {
        this._state.selectionHistoryIndex = restoreIndex;
      }
      return;
    }
    this._state.pendingHistoryRestore = null;
    const history = appendSelectionHistoryEntry(
      this._state.selectionHistoryEntries,
      this._state.selectionHistoryIndex,
      entry,
    );
    this._state.selectionHistoryEntries = history.entries;
    this._state.selectionHistoryIndex = history.index;
  }

  handleSelectionChanged(detail) {
    const selection = detail?.state?.selection;
    if (!selection || typeof selection !== "object") {
      return;
    }
    this.recordSelectionForHistory(selection, {
      runtimeLayers: runtimeLayersFromSelectionEventDetail(
        detail,
        this._mapShellProjection,
      ),
    });
    this.scheduleRender();
  }

  restoreSelectionHistoryEntry(direction) {
    const history = normalizeSelectionHistoryEntries(this._state.selectionHistoryEntries);
    const currentIndex = normalizeSelectionHistoryIndex(
      history,
      this._state.selectionHistoryIndex,
    );
    const targetIndex = currentIndex + (direction === "forward" ? 1 : -1);
    const entry = history[targetIndex] || null;
    if (!entry?.key) {
      return;
    }
    const target = focusWorldPointRequestTarget(
      {
        elementKind: entry.target?.elementKind,
        characterIdentityKey: entry.target?.characterIdentityKey,
        worldX: entry.worldX,
        worldZ: entry.worldZ,
        pointKind: entry.pointKind,
        pointLabel: entry.pointLabel || entry.label,
      },
      "navigate",
    );
    if (!target || !dispatchFocusWorldPointRequest(componentEventTarget(this), target)) {
      return;
    }
    this._state.selectionHistoryEntries = history;
    this._state.selectionHistoryIndex = targetIndex;
    this._state.pendingHistoryRestore = {
      index: targetIndex,
      key: entry.key,
      worldX: entry.worldX,
      worldZ: entry.worldZ,
    };
    this.scheduleRender();
  }

  currentSelectionBookmarkAction() {
    return buildLocationBookmarkAction(this._mapShellProjection, {
      zoneCatalog: this._state.zoneCatalog,
    });
  }

  copyCurrentSelectionBookmark() {
    const action = this.currentSelectionBookmarkAction();
    if (!action) {
      globalThis.__fishystuffToast?.warning?.(
        mapText("info.location.bookmark_unavailable"),
      );
      return;
    }
    void this._copyLocationBookmark(action.bookmark)
      .then(() => {
        globalThis.__fishystuffToast?.success?.(
          mapText("bookmarks.toast.copied_single"),
        );
      })
      .catch(() => {
        globalThis.__fishystuffToast?.error?.(
          mapText("bookmarks.toast.clipboard_unavailable"),
        );
      });
  }

  createCurrentSelectionBookmark() {
    const action = this.currentSelectionBookmarkAction();
    if (!action) {
      return null;
    }
    return createLocationBookmark(
      action,
      componentEventTarget(this),
    );
  }

  render() {
    const projection = this._mapShellProjection;
    const viewSignals = this.infoViewSignals(projection);
    const viewModel = buildInfoViewModel(viewSignals, {
      zoneCatalog: this._state.zoneCatalog,
      zoneLootSummary: this._state.zoneLootSummary,
      zoneLootStatus: this._state.zoneLootStatus,
      zoneLootConditionSelection: this.zoneLootConditionSelection(projection),
      lootRateDisplayMode: this.lootRateDisplayMode(projection),
      zoneLootSortBy: this.zoneLootSortBy(projection),
      zoneLootSortOrder: this.zoneLootSortOrder(projection),
      tradeNpcMapCatalog: this._state.tradeNpcMapCatalog,
      tradeNpcMapStatus: this._state.tradeNpcMapStatus,
    });
    setOptionalAttribute(
      this,
      "data-catch-profile-state",
      catchProfileStateForInfoViewModel(viewModel, this._state.zoneLootStatus),
    );
    setOptionalAttribute(
      this,
      "data-selection-generation",
      projection?.runtime?.selection?.detailsGeneration,
    );
    setOptionalAttribute(this, "data-active-pane-id", viewModel.activePaneId);
    const activePointSampleSection = (viewModel.activePane?.sections || []).find(
      (section) => trimString(section?.kind) === "point-samples",
    );
    const activePointSampleSectionKey = activePointSampleSection
      ? pointSampleSectionKey(activePointSampleSection)
      : "";
    if (this._state.pointSampleSectionKey !== activePointSampleSectionKey) {
      this._state.pointSampleSectionKey = activePointSampleSectionKey;
      this._state.pointSamplePage = 0;
    }
    this._state.pointSamplePage = normalizePointSamplePage(
      this._state.pointSamplePage,
      Array.isArray(activePointSampleSection?.rows) ? activePointSampleSection.rows : [],
    );
    setTextContent(this._elements?.title, viewModel.descriptor.title);
    setTextContent(this._elements?.statusText, viewModel.descriptor.statusText);
    const locationBookmarkSection = locationBookmarkSectionFromViewModel(viewModel);
    const canUseBookmark = locationBookmarkSection?.canUseBookmark === true;
    setBooleanProperty(this._elements?.status, "disabled", !canUseBookmark);
    setOptionalAttribute(
      this._elements?.status,
      "aria-label",
      canUseBookmark
        ? `${mapText("info.location.copy_bookmark")}: ${viewModel.descriptor.statusText}`
        : "",
    );
    setOptionalAttribute(
      this._elements?.status,
      "data-fishy-tooltip",
      canUseBookmark ? mapText("info.location.copy_bookmark") : "",
    );
    setMarkup(
      this._elements?.titleIcon,
      viewModel.descriptor.titleIcon,
      spriteIcon(viewModel.descriptor.titleIcon || "information-circle", "size-5"),
    );
    setMarkup(
      this._elements?.statusIcon,
      viewModel.descriptor.statusIcon,
      spriteIcon(viewModel.descriptor.statusIcon || "information-circle", "size-4"),
    );
    updateHistoryNavButton(
      this._elements?.historyBack,
      selectionHistoryTargetEntry(
        this._state.selectionHistoryEntries,
        this._state.selectionHistoryIndex,
        "back",
      ),
      "info.history.previous",
    );
    updateHistoryNavButton(
      this._elements?.historyForward,
      selectionHistoryTargetEntry(
        this._state.selectionHistoryEntries,
        this._state.selectionHistoryIndex,
        "forward",
      ),
      "info.history.next",
    );
    setBooleanProperty(this._elements?.tabs, "hidden", viewModel.panes.length === 0);
    setMarkup(
      this._elements?.tabs,
      JSON.stringify(viewModel.panes.map((pane) => [pane.id, pane.label, pane.id === viewModel.activePaneId ? 1 : 0])),
      viewModel.panes.map((pane) => tabButtonMarkup(pane, viewModel.activePaneId)).join(""),
    );
    const sectionOptions = {
      pointSamplePage: this._state.pointSamplePage,
      threeDEnabled: this.threeDEnabled(projection),
      tradeManagerFilter: this._state.tradeManagerFilter,
    };
    this.parkCharacterActivitySurface();
    setMarkup(
      this._elements?.panel,
      panelRenderKey(viewModel, this._state.pointSamplePage, sectionOptions),
      viewModel.empty
        ? emptyPanelMarkup()
        : `<section class="space-y-3">${(viewModel.activePane?.sections || []).map((section) => sectionMarkup(section, sectionOptions)).join("")}</section>`,
    );
    const visibleCharacterIdentityKey = visibleCharacterActivityIdentity(
      viewModel,
      projection,
    );
    if (visibleCharacterIdentityKey) {
      this.mountCharacterActivitySurface(visibleCharacterIdentityKey);
    }
    this.selectCharacterActivity(visibleCharacterIdentityKey);
  }

  scheduleRender() {
    if (this._renderQueued) {
      return;
    }
    this._renderQueued = true;
    const run = () => {
      if (!this._renderQueued) {
        return;
      }
      this._renderQueued = false;
      this.render();
    };
    if (typeof globalThis.queueMicrotask === "function") {
      globalThis.queueMicrotask(run);
      return;
    }
    if (typeof globalThis.Promise === "function") {
      globalThis.Promise.resolve().then(run);
      return;
    }
    run();
  }

  scheduleSelectionDataRefresh() {
    if (this._selectionDataRefreshQueued) {
      return;
    }
    this._selectionDataRefreshQueued = true;
    const run = () => {
      this._selectionDataRefreshQueued = false;
      if (!this._shell) {
        return;
      }
      const projection = this._mapShellProjection;
      const refreshKey = selectionDataRefreshKey(projection);
      if (this._state.selectionDataRefreshKey === refreshKey) {
        return;
      }
      this._state.selectionDataRefreshKey = refreshKey;
      void this.refreshZoneLootSummary();
    };
    if (typeof globalThis.queueMicrotask === "function") {
      globalThis.queueMicrotask(run);
      return;
    }
    if (typeof globalThis.Promise === "function") {
      globalThis.Promise.resolve().then(run);
      return;
    }
    run();
  }

  _cancelZoneLootRequest() {
    this._state.zoneLootRequestToken += 1;
    this._zoneLootAbortController?.abort();
    this._zoneLootAbortController = null;
  }

  async refreshZoneLootSummary({ force = false } = {}) {
    const selection = this._mapShellProjection?.runtime?.selection || null;
    const zoneRgb = zoneRgbFromSelection(selection);
    if (!Number.isInteger(zoneRgb) || zoneRgb < 0) {
      this._cancelZoneLootRequest();
      this._state.zoneLootRgb = null;
      this._state.zoneLootStatus = "idle";
      this._state.zoneLootSummary = null;
      this._state.zoneLootConditionSelection = {};
      this.scheduleRender();
      return;
    }
    const calculatorContext = this._calculatorMapContextProjection;
    const overlay = calculatorContext?.overlay ?? null;
    if (
      calculatorContext?.state !== "ready"
      || !overlay
      || !["ready", "missing"].includes(overlay.state)
    ) {
      this._cancelZoneLootRequest();
      this._state.zoneLootRgb = zoneRgb;
      const loading = calculatorContext?.state === "loading" || overlay?.state === "loading";
      this._state.zoneLootStatus = loading ? "loading" : "error";
      this._state.zoneLootSummary = loading
        ? null
        : {
            available: false,
            zoneName: "",
            profileLabel: "",
            note: mapText("info.zone_loot.unavailable"),
            groups: [],
            speciesRows: [],
          };
      this.scheduleRender();
      return;
    }
    if (
      !force &&
      this._state.zoneLootRgb === zoneRgb &&
      (this._state.zoneLootStatus === "loading" ||
        this._state.zoneLootStatus === "loaded" ||
        this._state.zoneLootStatus === "error")
    ) {
      return;
    }
    if (force || this._state.zoneLootRgb !== zoneRgb) {
      this._state.zoneLootConditionSelection = {};
    }
    this._state.zoneLootRgb = zoneRgb;
    this._state.zoneLootStatus = "loading";
    this._state.zoneLootSummary = null;
    this.scheduleRender();

    this._zoneLootAbortController?.abort();
    const abortController = new AbortController();
    this._zoneLootAbortController = abortController;
    const requestToken = this._state.zoneLootRequestToken + 1;
    this._state.zoneLootRequestToken = requestToken;
    try {
      const summary = await this._loadZoneLootSummary(zoneRgb, {
        calculatorOverlayCurrent: overlay.state === "ready" ? overlay.document : null,
        signal: abortController.signal,
      });
      if (this._state.zoneLootRequestToken !== requestToken || this._state.zoneLootRgb !== zoneRgb) {
        return;
      }
      this._state.zoneLootSummary = summary;
      this._state.zoneLootStatus = "loaded";
    } catch (error) {
      if (this._state.zoneLootRequestToken !== requestToken || this._state.zoneLootRgb !== zoneRgb) {
        return;
      }
      this._state.zoneLootSummary = {
        available: false,
        zoneName: "",
        profileLabel: "",
        note: trimString(error?.message) || mapText("info.zone_loot.unavailable"),
        groups: [],
        speciesRows: [],
      };
      this._state.zoneLootStatus = "error";
    } finally {
      if (this._zoneLootAbortController === abortController) {
        this._zoneLootAbortController = null;
      }
    }
    this.scheduleRender();
  }
}

export function registerFishyMapInfoPanelElement(registry = globalThis.customElements) {
  if (!registry || typeof registry.get !== "function" || typeof registry.define !== "function") {
    return false;
  }
  if (registry.get(INFO_PANEL_TAG_NAME)) {
    return true;
  }
  registry.define(INFO_PANEL_TAG_NAME, FishyMapInfoPanelElement);
  return true;
}

registerFishyMapInfoPanelElement();
